"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface SwitchV2Props {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (_checked: boolean) => void;
  label?: string;
  helperText?: string;
  errorText?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function SwitchV2({
  checked: controlledChecked,
  defaultChecked = false,
  onCheckedChange,
  label,
  helperText,
  errorText,
  disabled = false,
  id,
  className,
}: SwitchV2Props) {
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
  const isControlled = controlledChecked !== undefined;
  const isOn = isControlled ? controlledChecked : internalChecked;

  const uid = React.useId();
  const switchId = id ?? uid;
  const errorId = errorText ? `${switchId}-error` : undefined;
  const helperId = !errorText && helperText ? `${switchId}-helper` : undefined;
  const describedBy =
    [errorId, helperId].filter(Boolean).join(" ") || undefined;

  const toggle = () => {
    if (disabled) return;
    const next = !isOn;
    if (!isControlled) setInternalChecked(next);
    onCheckedChange?.(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className={cn(
          "v2-focus-ring relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors",
          isOn ? "bg-[var(--v2-accent)]" : "bg-slate-200",
          disabled && "cursor-not-allowed opacity-50"
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
            isOn ? "translate-x-5" : "translate-x-0.5"
          )}
        />
      </button>

      {(label || helperText || errorText) && (
        <div className="space-y-0.5">
          {label && (
            <label
              htmlFor={switchId}
              className={cn(
                "block cursor-pointer text-sm font-medium text-[var(--v2-text-strong)]",
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {label}
            </label>
          )}
          {errorText && (
            <span
              id={errorId}
              className="block text-xs text-red-600"
              role="alert"
            >
              {errorText}
            </span>
          )}
          {!errorText && helperText && (
            <span
              id={helperId}
              className="block text-xs text-[var(--v2-text-muted)]"
            >
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
